'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { sanitizeHexColor } from '@/lib/browser-image-tools';
import { convertWebpToMp4, formatFileSize, getBaseName } from '@/lib/browser-webp-tools';

type BackgroundMode = 'white' | 'black' | 'custom';
type FpsMode = 'keep' | '30' | '20' | '12';

export default function BrowserWebpToMp4() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [targetWidth, setTargetWidth] = useState(640);
  const [fpsMode, setFpsMode] = useState<FpsMode>('keep');
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('white');
  const [customHex, setCustomHex] = useState('#ffffff');

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  useEffect(() => {
    if (!source) {
      return;
    }

    setTargetWidth(source.metadata.width);
  }, [source]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be loaded.');
    }
  }

  async function runConversion() {
    if (!source) {
      setErrorMessage('Choose an animated WebP first.');
      return;
    }

    if (!source.metadata.animated) {
      setErrorMessage('This WebP is static. Use Video to WebP or WebP Optimizer instead.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    const background =
      backgroundMode === 'white'
        ? '#ffffff'
        : backgroundMode === 'black'
          ? '#000000'
          : sanitizeHexColor(customHex);

    try {
      const credits = await consumeToolUsage(
        'webp-to-mp4',
        'Credits are not available for another WebP to MP4 conversion right now.',
      );
      const outputBlob = await convertWebpToMp4({
        file: source.file,
        width: targetWidth,
        fps: fpsMode === 'keep' ? null : Number(fpsMode),
        background,
      });
      const nextOutputUrl = URL.createObjectURL(outputBlob);

      resetOutputState();
      setOutputUrl(nextOutputUrl);
      setDownloadName(`${getBaseName(source.file.name)}.mp4`);
      setResultSize(outputBlob.size);
      setCreditMessage(formatToolCreditSuccess('WebP to MP4 conversion', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP could not be converted to MP4.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Convert animated WebP to MP4</h2>
          <p>
            Turn animated WebP loops into a lighter MP4 for sharing, docs, and embeds. Transparent
            areas are flattened onto a matte background.
          </p>
        </div>
        <span className="tool-live-badge">MP4</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? source.file.name : 'Choose one animated WebP or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Animated WebP only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Output width</span>
              <input
                min="2"
                onChange={(event) => {
                  setTargetWidth(Math.max(2, Number(event.target.value) || 2));
                  resetOutputState();
                }}
                type="number"
                value={targetWidth}
              />
            </label>
            <label className="tool-live-field">
              <span>FPS mode</span>
              <select
                onChange={(event) => {
                  setFpsMode(event.target.value as FpsMode);
                  resetOutputState();
                }}
                value={fpsMode}
              >
                <option value="keep">Keep</option>
                <option value="30">30 fps</option>
                <option value="20">20 fps</option>
                <option value="12">12 fps</option>
              </select>
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Matte background</span>
              <select
                onChange={(event) => {
                  setBackgroundMode(event.target.value as BackgroundMode);
                  resetOutputState();
                }}
                value={backgroundMode}
              >
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            {backgroundMode === 'custom' ? (
              <label className="tool-live-field">
                <span>Custom color</span>
                <input
                  onChange={(event) => {
                    setCustomHex(sanitizeHexColor(event.target.value));
                    resetOutputState();
                  }}
                  type="text"
                  value={customHex}
                />
              </label>
            ) : null}
          </div>

          <div className="tool-live-panel-note">
            <strong>Transparency note</strong>
            <span>MP4 does not keep WebP transparency, so the animation is flattened.</span>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? 'Converting…' : 'Convert to MP4'}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download MP4
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Conversion details</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Frames</strong>
              <span>{source?.metadata.frameCount ?? 'No file loaded'}</span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>
                {source?.metadata.animated
                  ? `${(source.metadata.durationMs / 1000).toFixed(2)}s`
                  : 'Static'}
              </span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
